// WbAgentsPage — /weighbridge/settings/agents (admin only).
//
// Phase C2 Group 3. Lists the tenant's WB agents (the on-prem edge
// processes that bridge hardware to the cloud) with their live
// heartbeat-derived status and the admin tooling for pairing.
//
// Pairing flow:
//   1. Admin clicks "Pair a New Agent" → modal collects a hostname/label.
//   2. POST /wb/agents creates the agent record (status='unpaired').
//   3. POST /wb/agents/:enc_id/pairing-code generates a 15-minute code.
//   4. Plaintext code shown in a monospace box with copy + countdown.
//   5. The physical agent installer redeems the code (separate
//      unauthenticated endpoint on the `wb_agent` module).
//
// Status badges and "last heartbeat" derive entirely from the server's
// computed `status` field — the server already encodes the "paired AND
// recent heartbeat" rule so the client just maps the enum to a colour.

import { useEffect, useState, type FormEvent } from 'react';
import { Plus, Server, Copy, RefreshCw } from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Badge } from '../../../components/ui/Badge/Badge';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { Modal } from '../../../components/ui/Modal/Modal';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import {
  useAssignAgentWeighbridge,
  useCreateAgent,
  useDeactivateAgent,
  useGeneratePairingCode,
  useWbAgents,
  type WbAgent,
} from '../hooks/useWbAgents';
import { useWbWeighbridges } from '../hooks/useWbWeighbridges';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbAgentsPage.module.css';

// -- Helpers ---------------------------------------------------------------

function fmtRelative(iso: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const deltaSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const deltaMin = Math.floor(deltaSec / 60);
  if (deltaMin < 60) return `${deltaMin} min ago`;
  const deltaHr = Math.floor(deltaMin / 60);
  if (deltaHr < 24) return `${deltaHr}h ago`;
  const deltaDay = Math.floor(deltaHr / 24);
  return `${deltaDay}d ago`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function StatusBadge({ agent }: { agent: WbAgent }): JSX.Element {
  // `status` carries the pairing lifecycle; live connectivity is `is_online`
  // (heartbeat freshness). An unpaired agent has never connected, so show that
  // distinctly; otherwise the badge reflects real presence.
  if (agent.status === 'unpaired') return <Badge variant="muted">Unpaired</Badge>;
  return agent.is_online ? (
    <Badge variant="success">Online</Badge>
  ) : (
    <Badge variant="danger">Offline</Badge>
  );
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

// -- Weighbridge assignment cell -------------------------------------------

interface WeighbridgeOption {
  enc_id: string;
  code: string;
  name: string;
}

interface AgentWeighbridgeCellProps {
  agent: WbAgent;
  weighbridges: WeighbridgeOption[];
  onAssign: (weighbridgeEncId: string | null) => void;
  saving: boolean;
}

function AgentWeighbridgeCell({
  agent,
  weighbridges,
  onAssign,
  saving,
}: AgentWeighbridgeCellProps): JSX.Element {
  // enc_id is re-encrypted (random IV) on every response, so the agent's
  // weighbridge_enc_id (from GET /wb/agents) never equals the options' enc_ids
  // (from GET /wb/weighbridges) even for the same bridge. Key the selected
  // state on the stable `code` and resolve back to this list's enc_id for the
  // <select> value so the assignment doesn't visually reset after save.
  const selectedEncId =
    agent.weighbridge_code !== null
      ? (weighbridges.find((w) => w.code === agent.weighbridge_code)?.enc_id ??
        '')
      : '';
  return (
    <select
      className={styles.weighbridgeSelect}
      value={selectedEncId}
      disabled={saving}
      aria-label={`Weighbridge for ${agent.hostname ?? 'agent'}`}
      data-testid={`wb-agent-weighbridge-${agent.enc_id}`}
      onChange={(e) => onAssign(e.target.value === '' ? null : e.target.value)}
    >
      <option value="">— Unassigned —</option>
      {weighbridges.map((w) => (
        <option key={w.enc_id} value={w.enc_id}>
          {w.name}
        </option>
      ))}
    </select>
  );
}

// -- Pairing-code display sub-component ------------------------------------

interface PairingCodeDisplayProps {
  code: string;
  expiresAt: string;
  onRegenerate?: () => void;
  regenerateLoading?: boolean;
}

function PairingCodeDisplay({
  code,
  expiresAt,
  onRegenerate,
  regenerateLoading = false,
}: PairingCodeDisplayProps): JSX.Element {
  // Re-render every second to drive the countdown. The 1s setInterval
  // is cleared on unmount (and when `expiresAt` changes after a
  // regenerate) so we never leak timers. Using setInterval over rAF
  // because seconds-resolution is the user-visible granularity and rAF
  // would tick 60× faster than needed.
  const [, setTick] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);

  // Reset the "Copied!" pill after 2s.
  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(id);
  }, [copied]);

  const expiryMs = Date.parse(expiresAt);
  const remaining = Number.isNaN(expiryMs)
    ? 0
    : Math.max(0, Math.floor((expiryMs - Date.now()) / 1000));
  const expired = remaining <= 0;

  async function handleCopy(): Promise<void> {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
        setCopied(true);
      }
    } catch {
      // Clipboard access can be denied (e.g. headless) — silently no-op.
    }
  }

  const mm = Math.floor(remaining / 60);
  const ss = remaining % 60;

  return (
    <div className={styles.codePanel}>
      {expired ? (
        <p className={styles.codeExpired}>
          Code expired — generate a new one
        </p>
      ) : (
        <>
          <div className={styles.codeValue} aria-label="Pairing code">
            {code}
          </div>
          <div className={styles.codeMeta}>
            Expires in <strong>{pad2(mm)}:{pad2(ss)}</strong>
          </div>
        </>
      )}
      <div className={styles.codeActions}>
        {!expired ? (
          <Button variant="secondary" icon={Copy} size="sm" onClick={handleCopy}>
            Copy code
          </Button>
        ) : null}
        {onRegenerate ? (
          <Button
            variant={expired ? 'primary' : 'ghost'}
            icon={RefreshCw}
            size="sm"
            onClick={onRegenerate}
            loading={regenerateLoading}
          >
            {expired ? 'Generate new code' : 'Regenerate'}
          </Button>
        ) : null}
      </div>
      {copied ? (
        <span className={styles.copyConfirm} role="status">
          Copied!
        </span>
      ) : null}
    </div>
  );
}

// -- Page -------------------------------------------------------------------

interface ActiveCode {
  code: string;
  expiresAt: string;
  agentEncId: string;
}

export function WbAgentsPage(): JSX.Element {
  const agentsQuery = useWbAgents();
  const agents = agentsQuery.data?.agents ?? [];

  const createAgent = useCreateAgent();
  const generateCode = useGeneratePairingCode();
  const deactivateAgent = useDeactivateAgent();
  const assignWeighbridge = useAssignAgentWeighbridge();

  const weighbridgesQuery = useWbWeighbridges();
  const weighbridges = (weighbridgesQuery.data?.weighbridges ?? []).map((w) => ({
    enc_id: w.enc_id,
    code: w.code,
    name: w.name,
  }));

  const [activeCode, setActiveCode] = useState<ActiveCode | null>(null);
  const [pairModalOpen, setPairModalOpen] = useState(false);
  const [pairLabel, setPairLabel] = useState('');
  const [pairError, setPairError] = useState<string | null>(null);
  const [confirmDeactivateFor, setConfirmDeactivateFor] =
    useState<WbAgent | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  function openPairModal(): void {
    setPairLabel('');
    setPairError(null);
    setPairModalOpen(true);
  }

  function closePairModal(): void {
    if (createAgent.isPending || generateCode.isPending) return;
    setPairModalOpen(false);
    setPairError(null);
  }

  async function handlePairSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setPairError(null);
    const name = pairLabel.trim();
    if (name.length === 0) {
      setPairError('Hostname or label is required.');
      return;
    }
    try {
      const created = await createAgent.mutateAsync({ name });
      const result = await generateCode.mutateAsync({
        agentEncId: created.agent.enc_id,
      });
      setActiveCode({
        code: result.pairing_code,
        expiresAt: result.expires_at,
        agentEncId: created.agent.enc_id,
      });
      setPairModalOpen(false);
    } catch (err) {
      setPairError(extractErrorMessage(err));
    }
  }

  async function handleGenerateForExisting(agent: WbAgent): Promise<void> {
    setActionError(null);
    try {
      const result = await generateCode.mutateAsync({ agentEncId: agent.enc_id });
      setActiveCode({
        code: result.pairing_code,
        expiresAt: result.expires_at,
        agentEncId: agent.enc_id,
      });
    } catch (err) {
      setActionError(extractErrorMessage(err));
    }
  }

  async function handleRegenerate(): Promise<void> {
    if (!activeCode) return;
    setActionError(null);
    try {
      const result = await generateCode.mutateAsync({
        agentEncId: activeCode.agentEncId,
      });
      setActiveCode({
        code: result.pairing_code,
        expiresAt: result.expires_at,
        agentEncId: activeCode.agentEncId,
      });
    } catch (err) {
      setActionError(extractErrorMessage(err));
    }
  }

  async function handleAssignWeighbridge(
    agent: WbAgent,
    weighbridgeEncId: string | null,
  ): Promise<void> {
    setActionError(null);
    try {
      await assignWeighbridge.mutateAsync({
        agentEncId: agent.enc_id,
        weighbridgeEncId,
      });
    } catch (err) {
      setActionError(extractErrorMessage(err));
    }
  }

  async function handleConfirmDeactivate(): Promise<void> {
    if (!confirmDeactivateFor) return;
    setActionError(null);
    try {
      await deactivateAgent.mutateAsync({
        agentEncId: confirmDeactivateFor.enc_id,
      });
      // Clear the active code if it belonged to this agent.
      if (activeCode?.agentEncId === confirmDeactivateFor.enc_id) {
        setActiveCode(null);
      }
      setConfirmDeactivateFor(null);
    } catch (err) {
      setActionError(extractErrorMessage(err));
    }
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Weighbridge Agents"
        subtitle="On-prem edge processes that bridge bridge hardware to the cloud"
        actions={
          <Button variant="primary" icon={Plus} onClick={openPairModal}>
            Pair a New Agent
          </Button>
        }
      />

      {agentsQuery.isError ? (
        <p className={styles.error}>{extractErrorMessage(agentsQuery.error)}</p>
      ) : null}

      {actionError ? <p className={styles.error}>{actionError}</p> : null}

      {/* Active pairing-code display (post-create or post-generate). */}
      {activeCode ? (
        <Card
          title="New pairing code"
          actions={
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActiveCode(null)}
            >
              Dismiss
            </Button>
          }
        >
          <p className={styles.pairCopy}>
            Enter this code into the Tricivo Weighbridge Agent installer on
            the bridge PC. Codes expire in 15 minutes and can only be used
            once.
          </p>
          <PairingCodeDisplay
            code={activeCode.code}
            expiresAt={activeCode.expiresAt}
            onRegenerate={handleRegenerate}
            regenerateLoading={generateCode.isPending}
          />
        </Card>
      ) : null}

      <Card flush>
        {agentsQuery.isLoading ? (
          <div className={styles.loadingRow}>
            <Spinner label="Loading agents" />
          </div>
        ) : agents.length === 0 ? (
          <EmptyState
            icon={Server}
            title="No agents yet"
            description="Install the Tricivo Weighbridge Agent on the bridge PC, then click Pair a New Agent."
            action={
              <Button variant="primary" icon={Plus} onClick={openPairModal}>
                Pair a New Agent
              </Button>
            }
          />
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Hostname</th>
                <th>Version</th>
                <th>Weighbridge</th>
                <th>Status</th>
                <th>Last heartbeat</th>
                <th>Paired since</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={a.enc_id} data-enc-id={a.enc_id}>
                  <td>
                    {a.hostname ?? <span className={styles.muted}>—</span>}
                  </td>
                  <td>
                    {a.version ?? <span className={styles.muted}>—</span>}
                  </td>
                  <td>
                    <AgentWeighbridgeCell
                      agent={a}
                      weighbridges={weighbridges}
                      saving={
                        assignWeighbridge.isPending &&
                        assignWeighbridge.variables?.agentEncId === a.enc_id
                      }
                      onAssign={(wbEncId) => {
                        void handleAssignWeighbridge(a, wbEncId);
                      }}
                    />
                  </td>
                  <td>
                    <StatusBadge agent={a} />
                  </td>
                  <td>{fmtRelative(a.last_heartbeat_at)}</td>
                  <td>{fmtDate(a.paired_at)}</td>
                  <td className={styles.actionsCell}>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={RefreshCw}
                      onClick={() => handleGenerateForExisting(a)}
                      loading={
                        generateCode.isPending &&
                        generateCode.variables?.agentEncId === a.enc_id
                      }
                    >
                      {a.status === 'unpaired' &&
                      !a.pairing_code_expires_at
                        ? 'Generate Code'
                        : 'Regenerate Code'}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setConfirmDeactivateFor(a)}
                    >
                      Deactivate
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Instructional bottom card — mirrors the install copy. */}
      <Card title="How to pair an agent">
        <p className={styles.pairCopy}>
          <strong>1.</strong> Install the Tricivo Weighbridge Agent on the
          bridge PC.
          <br />
          <strong>2.</strong> Click <em>Pair a New Agent</em> above and enter
          the hostname (or any friendly label) so this row stays identifiable
          before the agent connects.
          <br />
          <strong>3.</strong> Copy the generated code into the agent installer.
          Codes expire in 15 minutes — generate a fresh one if it lapses.
        </p>
      </Card>

      {/* Pair-new-agent modal -------------------------------------------- */}
      <Modal
        open={pairModalOpen}
        onClose={closePairModal}
        title="Pair a New Agent"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={closePairModal}
              disabled={createAgent.isPending || generateCode.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="wb-agents-pair-form"
              loading={createAgent.isPending || generateCode.isPending}
            >
              Generate pairing code
            </Button>
          </>
        }
      >
        <form id="wb-agents-pair-form" onSubmit={handlePairSubmit}>
          <div className={styles.modalField}>
            <label htmlFor="wb-agents-pair-label">Hostname or label</label>
            <input
              id="wb-agents-pair-label"
              type="text"
              value={pairLabel}
              onChange={(e) => setPairLabel(e.target.value)}
              placeholder="e.g. BRIDGE-PC-01"
              maxLength={255}
              autoFocus
            />
          </div>
          {pairError ? <p className={styles.modalError}>{pairError}</p> : null}
        </form>
      </Modal>

      {/* Deactivate confirmation ----------------------------------------- */}
      <ConfirmModal
        open={confirmDeactivateFor !== null}
        title="Deactivate this agent?"
        message="The agent will need to be re-paired to come back online. Any active pairing code will be invalidated."
        confirmLabel="Deactivate"
        variant="danger"
        onConfirm={handleConfirmDeactivate}
        onCancel={() => setConfirmDeactivateFor(null)}
        loading={deactivateAgent.isPending}
      />
    </div>
  );
}
